import api from './api';

/** Áudio de referência do comercial. O fingerprinter indexa em até 30s
 *  quando fingerprint_status = 'pending'. */
export interface AnuncioAudio {
  id: number;
  anuncio_id: number;
  nome?: string;
  amazonpath: string;
  duracao?: number;
  fingerprint_status?: string;
  fingerprint_error?: string;
  created_at?: string;
}

export interface Anuncio {
  id: number;
  nome: string;
  ativo?: boolean;
  /** Ids das emissoras em que este anúncio é monitorado. */
  emissora_ids?: number[];
  amazonpath?: string;
  datapublicacao?: string;
  dataremocao?: string;
  transcricao?: string;
  tipo?: string;
  titulo?: string;
  duracao?: number;
  precisao?: number;
  mercado?: string;
  marca_id?: number;
  item_id?: number;
  anunciante_id?: number;
  setor_id?: number;
  categoria_id?: number;
  programa_id?: number;
  iniciovigencia?: string;
  fimvigencia?: string;
  palavraschave?: string;
  marca?: { id: number; nome: string };
  item?: { id: number; nome: string };
  anunciante?: { id: number; nome: string };
  setor?: { id: number; nome: string };
  categoria?: { id: number; nome: string };
  programa?: { id: number; nome: string };
}

export interface AnuncioCreate {
  nome: string;
  ativo?: boolean;
  /** Emissoras a monitorar — o backend sincroniza anuncio_emissora. */
  emissoras?: number[];
  amazonpath?: string;
  datapublicacao?: string;
  dataremocao?: string;
  transcricao?: string;
  tipo?: string;
  titulo?: string;
  duracao?: number;
  precisao?: number;
  mercado?: string;
  marca_id?: number;
  item_id?: number;
  anunciante_id?: number;
  setor_id?: number;
  categoria_id?: number;
  programa_id?: number;
  iniciovigencia?: string;
  fimvigencia?: string;
  palavraschave?: string;
}

export interface AnuncioUpdate {
  nome?: string;
  ativo?: boolean;
  /** Omitido = não mexe nos vínculos; lista vazia = remove todos. */
  emissoras?: number[];
  amazonpath?: string;
  datapublicacao?: string;
  dataremocao?: string;
  transcricao?: string;
  tipo?: string;
  titulo?: string;
  duracao?: number;
  precisao?: number;
  mercado?: string;
  marca_id?: number;
  item_id?: number;
  anunciante_id?: number;
  setor_id?: number;
  categoria_id?: number;
  programa_id?: number;
  iniciovigencia?: string;
  fimvigencia?: string;
  palavraschave?: string;
}

export interface AnuncioFilter {
  nome?: string;
  tipo?: string;
  anunciante_id?: number;
  marca_id?: number;
}

/** Remove campos vazios antes de enviar ao backend.
 *
 *  Campo numérico não preenchido no formulário chega aqui como string vazia, e o
 *  Pydantic devolve 422 ("unable to parse string as an integer") em duracao,
 *  setor_id e categoria_id — travando o cadastro inteiro. Omitir o campo faz o
 *  backend usar o default (None), que é o comportamento desejado.
 *
 *  Não usa filtro por falsy: 0 e false são valores legítimos (ativo=false, por ex.).
 */
function limparPayload<T extends Record<string, any>>(data: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(data).filter(([, v]) => v !== '' && v !== undefined && v !== null)
  ) as Partial<T>;
}

export const AnunciosService = {
  getAnuncios: async (filter: AnuncioFilter = {}, skip = 0, limit = 100) => {
    const { nome, tipo, anunciante_id, marca_id } = filter;
    const params = { skip, limit, nome, tipo, anunciante_id, marca_id };
    const response = await api.get('/anuncios', { params });
    return response.data;
  },

  getAnuncio: async (id: number) => {
    const response = await api.get(`/anuncios/${id}`);
    return response.data;
  },

  createAnuncio: async (anuncio: AnuncioCreate) => {
    const response = await api.post('/anuncios', limparPayload(anuncio));
    return response.data;
  },

  updateAnuncio: async (id: number, anuncio: AnuncioUpdate) => {
    const response = await api.put(`/anuncios/${id}`, limparPayload(anuncio));
    return response.data;
  },

  deleteAnuncio: async (id: number) => {
    const response = await api.delete(`/anuncios/${id}`);
    return response.data;
  },

  getTipos: async () => {
    const response = await api.get('/anuncios/tipos');
    return response.data;
  },

  // ── Áudios do anúncio ──────────────────────────────────────────────────────

  listAudios: async (anuncioId: number): Promise<AnuncioAudio[]> => {
    const response = await api.get(`/anuncios/${anuncioId}/audios`);
    return response.data;
  },

  /** Envia o MP3 de referência. Cria anuncio_audio com fingerprint_status='pending';
   *  o fingerprinter indexa sozinho em até 30 segundos. */
  uploadAudio: async (anuncioId: number, file: File, nome?: string): Promise<AnuncioAudio> => {
    const form = new FormData();
    form.append('file', file);
    if (nome) form.append('nome', nome);
    const response = await api.post(`/anuncios/${anuncioId}/audios`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteAudio: async (anuncioId: number, audioId: number): Promise<AnuncioAudio> => {
    const response = await api.delete(`/anuncios/${anuncioId}/audios/${audioId}`);
    return response.data;
  },
};

export default AnunciosService; 